import { useEffect, useRef } from 'react';
import { useUpdateUiSettings } from '@/entities/profile';
import { useSettingsStore } from '@/entities/settings';
import { SettingStore, SettingStoreUserValues, UiSettings } from '@/entities/settings/models/types';

const DEBOUNCE_DELAY_MS = 500;

/**
 * Хук синхронизации настроек интерфейса пользователя с бэкендом.
 *
 * Следит за изменениями пользовательских настроек в сторе и отправляет их
 * на сервер через PUT /profile/ui-settings с дебаунсом 500 мс.
 * Первоначальная инициализация (при монтировании) пропускается — сохраняются
 * только изменения, сделанные пользователем.
 */
export const useUiSettingsSync = (userId: string | null) => {
  const { mutate: saveUiSettings } = useUpdateUiSettings();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Предыдущие настройки для определения факта изменения
  const prevSettingsRef = useRef<SettingStoreUserValues | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Снимаем baseline до подписки — это уже инициализированные настройки пользователя
    // (initUserSettings выполняется до рендера children, т.е. до этого эффекта).
    // Любое последующее изменение стора означает действие пользователя.
    prevSettingsRef.current = useSettingsStore.getState().settings[userId] ?? null;

    // Подписываемся на изменения настроек конкретного пользователя в сторе
    const unsubscribe = useSettingsStore.subscribe((state: SettingStore) => {
      const settings = state.settings[userId] ?? null;

      // Пропускаем если настройки не изменились (ссылочное сравнение)
      if (settings === prevSettingsRef.current) return;
      prevSettingsRef.current = settings;

      if (!settings) return;

      // Отменяем предыдущий таймер дебаунса
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Запускаем сохранение с дебаунсом 500 мс
      debounceTimerRef.current = setTimeout(() => {
        saveUiSettings(settings satisfies UiSettings);
      }, DEBOUNCE_DELAY_MS);
    });

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [userId, saveUiSettings]);
};
